import { Typography } from '@mui/material';
import type { TrendPoint } from '../../api/revenue';

/**
 * Shared "suspect reading" marker for the revenue trend charts (portfolio /
 * site / machine). Under the include-and-flag model a suspect gaming day's
 * money IS counted in the line; this only draws a red review ring on that
 * point so it is visually obvious and can be reviewed later.
 *
 * (The confirm/amend resolution workflow that would clear a flag is deferred —
 * future development. This is display-only.)
 */

const SUSPECT_RED = '#d32f2f';

// Recharts calls the `dot` fn once per data point with { cx, cy, payload,
// index, ... }. A point whose payload.suspect is true renders a red ring;
// every other point renders nothing (an empty <g/> — Recharts expects a valid
// SVG element back, not null). Keyed by index so React is happy.
type RechartsDotProps = {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: TrendPoint;
};

export function renderSuspectDot(props: RechartsDotProps) {
  const { cx, cy, index, payload } = props;
  const key = `suspect-dot-${index ?? 0}`;
  if (!payload?.suspect || cx == null || cy == null) {
    return <g key={key} />;
  }
  return (
    <circle
      key={key}
      cx={cx}
      cy={cy}
      r={5}
      fill="#fff"
      stroke={SUSPECT_RED}
      strokeWidth={2}
    />
  );
}

/** Caption/legend note explaining the red marker, shown under a trend chart. */
export function SuspectTrendCaption() {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
      <span style={{ color: SUSPECT_RED }}>●</span> red = suspect reading (included in
      totals, pending review)
    </Typography>
  );
}
